import { useState } from "react";
import { toast } from "sonner";
import { addDays } from "date-fns";
import { Loader2, Navigation, Trash2, CalendarCheck2, Route, X, LocateFixed, ExternalLink, Milestone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/DatePicker";
import { StatusDot } from "@/components/StatusPill";
import { KIND_LABEL, toISO } from "@/lib/calendar";
import { formatDate } from "@/lib/format";
import { planTrip, formatKm, isAppleDevice } from "@/lib/geo";
import { cn } from "@/lib/utils";

const KindChip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    data-testid={testid}
    className={cn(
      "h-7 rounded-full border px-2 text-[11px] font-semibold transition-colors",
      active ? "border-transparent bg-[#0B0D10] text-white dark:bg-white dark:text-[#0B0D10]" : "bg-card text-muted-foreground hover:bg-accent"
    )}
  >
    {children}
  </button>
);

/**
 * TripPanel – turplanlegging v1.
 *  selection : Map<machine_id, {kind, order}>
 *  plan      : svar frå /api/geo/route-plan (stops i nærmaste-først-rekkjefølgje)
 */
export const TripPanel = ({ selection, machinesById, plan, planBusy, userPos, onLocate, onSetKind, onRemove, onClear, onPlanned, onOpenMachine, compact }) => {
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPlan, setShowPlan] = useState(false);
  const apple = isAppleDevice();
  const count = selection.size;

  const submit = async () => {
    if (!date) return toast.error("Vel ein dato for turen.");
    setBusy(true);
    try {
      const stops = [...selection.entries()].map(([machine_id, s]) => ({ machine_id, kind: s.kind }));
      const r = await planTrip(stops, date, note.trim() || `Kunderunde ${formatDate(date)}`);
      const ok = r.planned?.length || 0;
      if (r.failed?.length) toast.warning(`${ok} planlagt, ${r.failed.length} feila.`);
      else toast.success(`${ok} frist${ok === 1 ? "" : "ar"} planlagt til ${formatDate(date)}.`);
      setShowPlan(false);
      setDate("");
      setNote("");
      onPlanned?.(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikkje planleggje. Prøv igjen.");
    } finally {
      setBusy(false);
    }
  };

  if (count === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 px-6 py-10 text-center" data-testid="trip-empty">
        <span className="grid h-12 w-12 place-items-center rounded-full bg-muted"><Route className="h-6 w-6 text-muted-foreground" /></span>
        <p className="font-heading text-sm font-semibold">Ingen maskiner i turen</p>
        <p className="max-w-xs text-xs text-muted-foreground">Trykk på pluss-knappen ved ei maskin i lista, eller opne ein pin og vel «Legg i tur». Vi sorterer stoppa nærmaste-først.</p>
      </div>
    );
  }

  const today = new Date();
  const quick = [
    { label: "I morgon", d: addDays(today, 1) },
    { label: "+2 dagar", d: addDays(today, 2) },
    { label: "+1 veke", d: addDays(today, 7) },
    { label: "+2 veker", d: addDays(today, 14) },
  ];

  return (
    <div className={cn("flex h-full flex-col", compact && "min-h-0")} data-testid="trip-panel">
      {/* Samandrag */}
      <div className="flex items-center gap-3 border-b px-3 py-2.5">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary text-[#0B0D10]"><Route className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold" data-testid="trip-summary">
            {count} maskin{count === 1 ? "" : "er"}{plan ? ` · ${plan.stops.length} stopp` : ""}
          </div>
          <div className="truncate text-xs text-muted-foreground">
            {planBusy ? "Reknar rekkjefølgje …" : plan ? `${formatKm(plan.total_km)} luftlinje${userPos ? " frå deg" : " (frå første stopp)"}` : ""}
          </div>
        </div>
        {!userPos && (
          <Button size="sm" variant="ghost" className="h-9 shrink-0" onClick={onLocate} data-testid="trip-locate" title="Start frå min posisjon">
            <LocateFixed /> <span className="hidden sm:inline">Frå meg</span>
          </Button>
        )}
        <Button size="iconSm" variant="ghost" className="shrink-0 text-muted-foreground" onClick={onClear} aria-label="Tøm tur" data-testid="trip-clear"><Trash2 className="h-4 w-4" /></Button>
      </div>

      {/* Stopp */}
      <div className="min-h-0 flex-1 overflow-y-auto p-2" data-testid="trip-stops">
        {planBusy && !plan ? (
          <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Reknar rekkjefølgje …</div>
        ) : (
          <ol className="space-y-1.5">
            {(plan?.stops || []).map((s) => (
              <li key={`${s.order}-${s.lat}`} className="rounded-xl border bg-card p-2" data-testid={`trip-stop-${s.order}`}>
                <div className="flex items-center gap-2">
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[#0B0D10] text-xs font-bold text-white dark:bg-white dark:text-[#0B0D10]">{s.order}</span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold">{s.customer_name || "Ukjend kunde"}</div>
                    <div className="truncate text-xs text-muted-foreground">{s.address || "—"}{s.approximate ? " · omtrentleg" : ""}</div>
                  </div>
                  <span className="shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                    {s.order === 1 && !userPos ? "start" : `+${formatKm(s.leg_km)}`}
                  </span>
                </div>
                <ul className="mt-1.5 space-y-1 pl-9">
                  {s.machines.map((m) => {
                    const sel = selection.get(m.id);
                    const both = (m.deadlines || []).length > 1;
                    return (
                      <li key={m.id} className="flex items-center gap-1.5 text-xs" data-testid={`trip-machine-${m.id}`}>
                        <StatusDot status={m.overall_status} className="shrink-0" />
                        <button type="button" onClick={() => onOpenMachine?.(machinesById.get(m.id))} className="min-w-0 flex-1 truncate text-left font-medium hover:underline">{m.label}</button>
                        {both ? (
                          <span className="flex shrink-0 gap-1">
                            {m.deadlines.map((d) => (
                              <KindChip key={d.kind} active={sel?.kind === d.kind} onClick={() => onSetKind?.(m.id, d.kind)} testid={`trip-kind-${m.id}-${d.kind}`}>
                                {d.kind === "annual" ? "Årsk." : KIND_LABEL[d.kind]}
                              </KindChip>
                            ))}
                          </span>
                        ) : (
                          <span className="shrink-0 text-muted-foreground">{KIND_LABEL[sel?.kind] || ""}</span>
                        )}
                        <button type="button" onClick={() => onRemove?.(m.id)} aria-label="Fjern" className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground" data-testid={`trip-remove-${m.id}`}>
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
            {plan?.skipped?.length > 0 && (
              <li className="rounded-xl border border-dashed p-2 text-xs text-muted-foreground">{plan.skipped.length} maskin(er) manglar plassering og er ikkje med i ruta.</li>
            )}
          </ol>
        )}
        {plan && <p className="px-1 pt-2 text-[11px] text-muted-foreground"><Milestone className="mr-1 inline h-3 w-3" />{plan.note}</p>}

        {/* Planlegg dato */}
        {showPlan && (
          <div className="mt-3 space-y-3 rounded-xl border bg-muted/40 p-3" data-testid="trip-plan-form">
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">Dato for turen</Label>
              <DatePicker value={date} onChange={setDate} clearable={false} presets={[]} title="Dato for turen" testid="trip-date" />
              <div className="flex flex-wrap gap-1.5 pt-1">
                {quick.map((q) => {
                  const iso = toISO(q.d);
                  return (
                    <button key={q.label} type="button" onClick={() => setDate(iso)} aria-pressed={iso === date} data-testid={`trip-quick-${q.label.replace(/[^a-z0-9]/gi, "").toLowerCase()}`}
                      className={cn("h-9 rounded-full border bg-card px-3 text-xs font-semibold transition-colors hover:bg-accent", iso === date && "border-transparent bg-[#0B0D10] text-white hover:bg-[#0B0D10] dark:bg-white dark:text-[#0B0D10]")}>
                      {q.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">Notat (valfritt)</Label>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="T.d. «Kunderunde Bergen sør»" className="resize-none" data-testid="trip-note" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="h-11 flex-1" onClick={() => setShowPlan(false)} disabled={busy} data-testid="trip-plan-cancel">Avbryt</Button>
              <Button className="h-11 flex-1" onClick={submit} disabled={busy || !date} data-testid="trip-plan-submit">
                {busy ? <Loader2 className="animate-spin" /> : <CalendarCheck2 />} Planlegg {count}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Handlingar */}
      {!showPlan && (
        <div className="grid grid-cols-2 gap-2 border-t p-2">
          <Button className="h-11" onClick={() => setShowPlan(true)} data-testid="trip-plan-open">
            <CalendarCheck2 /> Planlegg dato
          </Button>
          <Button asChild variant="outline" className="h-11" disabled={!plan?.links?.google}>
            <a href={apple ? plan?.links?.apple : plan?.links?.google} target="_blank" rel="noreferrer" data-testid="trip-navigate" aria-disabled={!plan}>
              <Navigation /> {apple ? "Apple Maps" : "Google Maps"}
            </a>
          </Button>
          {plan?.links && (
            <a href={apple ? plan.links.google : plan.links.apple} target="_blank" rel="noreferrer" className="col-span-2 inline-flex h-9 items-center justify-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground" data-testid="trip-navigate-alt">
              <ExternalLink className="h-3.5 w-3.5" /> Opne i {apple ? "Google Maps" : "Apple Maps"} i staden
            </a>
          )}
        </div>
      )}
    </div>
  );
};
